import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane/HelpPane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { dateIsBefore } from 'lib/datetime/dateUtils'
import React, { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  policy: App.CarHireCancellationPolicy
}

function CarHireCancellationPolicyText(props: Props) {
  const { policy } = props

  const showModal = useContext(ModalContext)
  const intl = useIntl()

  const policyMaxDate = useMemo(() => new Date(policy.maxDate), [policy])
  const available = useMemo(() => dateIsBefore(new Date(), policyMaxDate), [policyMaxDate])

  const freeCancellationTitle = intl.formatMessage({
    id: 'XQMTanH2U0-RpF9H3NAm',
    defaultMessage: 'Free cancellation',
    description: 'Modal title for car hire free cancellation policy in the car hire section',
  })
  const nonRefundableTitle = intl.formatMessage({
    id: 'Ys215UcYLcIRSiewJ1oU',
    defaultMessage: 'Non-refundable',
    description: 'Modal title for car hire non-refundable policy in the car hire section',
  })

  const onTextClick = () => {
    showModal(<Modal title={available ? freeCancellationTitle : nonRefundableTitle}>
      <VerticalSpacer gap={24}>
        <BodyText variant="large">
          {available && intl.formatMessage(
            {
              id: 'eIWNINK80drCgu7AjazF',
              defaultMessage: 'You can cancel for free up to {maxHours, plural, one {# hour} other {# hours}} before pick-up.',
              description: 'Free cancellation policy description with hours until pick-up',
            },
            { maxHours: policy.maxHours },
          )}
          {!available && intl.formatMessage(
            {
              id: '2zbhP98ZsdA-Sbp6nHhi',
              defaultMessage: 'Unfortunately, as the pick-up for your car hire is within the next {maxHours, plural, one {# hour} other {# hours}}, your booking is non-refundable.',
              description: 'Non-refundable policy description when pick-up is imminent',
            },
            { maxHours: policy.maxHours },
          )}
        </BodyText>
        <HelpPane />
      </VerticalSpacer>
    </Modal>)
  }

  return (
    <Clickable onClick={onTextClick}>
      <Group direction="horizontal" gap={4} verticalAlign="center">
        {available && <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="-KBHQ3s5sSVhtnLDvFp1"
            defaultMessage="<bold>Free cancellation</bold> until {date}"
            description="Inline free cancellation label with date for car hire"
            values={{
              bold: (chunks) => <BodyText as="span" variant="medium" colour="highlight-secondary" weight="bold">{chunks}</BodyText>,
              date: <FormatDateTime value={policyMaxDate} format="short-day-name-day-month" />,
            }}
          />
        </BodyText>}
        {!available && <BodyText variant="medium" colour="neutral-two">
          {nonRefundableTitle}
        </BodyText>}
        <LineInfoCircleIcon colour="neutral-one" size="XS" />
      </Group>
    </Clickable>
  )
}

export default CarHireCancellationPolicyText
